import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useFacilitiesByUserId } from "@/modules/facility/facilityHooks";
import { getUserIdFromToken } from "@/modules/user/userHooks";
import { AdvancedLeagueEditor } from "./advanced/AdvancedLeagueEditor";
import { wizardLayout } from "./advanced/wizardLayout";
import { useGetLeagueQuery } from "../api/endpoints/leagueDetail";
import { isLeagueEditable } from "./helpers";
import { LeaguesV2Events } from "../analytics/leaguesV2Events";
import { useTrackLeaguesV2View } from "../analytics/useTrackLeaguesV2View";

export function EditLeaguePage() {
  const navigate = useNavigate();
  const { leagueId } = useParams<{ leagueId: string }>();
  useTrackLeaguesV2View(LeaguesV2Events.VIEW_EDIT_LEAGUE, { leagueId });

  const [userFacilities] = useFacilitiesByUserId(getUserIdFromToken());
  const hasMultipleFacilities = (userFacilities?.length ?? 0) > 1;

  const { data, isLoading, isError } = useGetLeagueQuery(leagueId ?? "", {
    skip: !leagueId,
  });

  const detail = data?.leagueV2Detail;
  const editable =
    !!detail && isLeagueEditable(detail.status, detail.season?.startDateUtc);

  // "Loaded fine but the league exists and simply can't be edited" (finalized,
  // started, archived). That's the only case we silently send to the read-only
  // detail view. A load error or a not-found payload stays here and shows a
  // message, rather than bouncing the operator with no explanation.
  const loadedNotEditable = !isLoading && !isError && !!detail && !editable;
  const notFound = !isLoading && !isError && !!data && !detail;

  useEffect(() => {
    if (!leagueId) {
      navigate("/leagues", { replace: true });
      return;
    }
    if (loadedNotEditable) {
      navigate(`/leagues/${leagueId}`, { replace: true });
    }
  }, [leagueId, loadedNotEditable, navigate]);

  return (
    <div className="w-full">
      <div className={`${wizardLayout.shell} flex flex-col gap-6 px-8 py-4`}>
        <Button
          type="button"
          variant="link"
          onClick={() => navigate(`/leagues/${leagueId}`)}
          className="w-fit px-0 h-auto gap-1 text-sm font-medium text-text-muted hover:text-text-primary hover:no-underline"
        >
          <ChevronLeft data-icon="inline-start" />
          Back to League
        </Button>

        <div>
          <h1 className="text-2xl font-semibold text-text-primary brand-text tracking-tight">
            Edit League
          </h1>
        </div>

        {isError ? (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            Failed to load this league. Please refresh and try again.
          </div>
        ) : notFound ? (
          <div className="rounded-md border border-border bg-surface/50 px-4 py-3 text-sm text-text-muted">
            League not found.
          </div>
        ) : editable && leagueId ? (
          <AdvancedLeagueEditor
            mode="edit"
            leagueId={leagueId}
            hasMultipleFacilities={hasMultipleFacilities}
            onExit={() => navigate(`/leagues/${leagueId}`)}
            onGoToDashboard={() => navigate("/leagues")}
            onOpenLeague={(id) => navigate(`/leagues/${id}`)}
          />
        ) : (
          // Loading, or briefly while a not-editable league redirects to detail.
          <div className="flex flex-col gap-4">
            <Skeleton className="h-9 w-full rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        )}
      </div>
    </div>
  );
}
